// Filtres actifs de la liste des projets, montrés en étiquettes dans l'en-tête quand la colonne des filtres est masquée.

import { readAmountFilter } from "./amountFilter";

export type FilterChip = {
  key: string;
  kind:
    | "q"
    | "phase"
    | "category"
    | "source_type"
    | "country"
    | "hide_other"
    | "min_score"
    | "min_eur"
    | "min_eur_or_unknown"
    | "max_eur"
    | "created_after"
    | "created_before"
    | "news_after"
    | "to_specify"
    | "other";
  values: string[];
};

// Ordre d'affichage : la recherche, puis du plus large au plus étroit, puis les seuils et les dates.
const KINDS: [string, FilterChip["kind"]][] = [
  ["q", "q"],
  ["phase", "phase"],
  ["phase@in", "phase"],
  ["category", "category"],
  ["category@in", "category"],
  ["source_types@cs", "source_type"],
  ["country", "country"],
  ["country@in", "country"],
  ["category@neq", "hide_other"],
  ["score@gte", "min_score"],
  ["budget_eur@gte", "min_eur"],
  ["@or", "min_eur_or_unknown"],
  ["budget_eur@lte", "max_eur"],
  ["created_at@gte", "created_after"],
  ["created_at@lt", "created_before"],
  ["last_news_at@gte", "news_after"],
  ["to_specify", "to_specify"],
];

const empty = (value: unknown) => value == null || value === "";

// Liste au format de PostgREST : (a,b) ou ("a b","c").
const listItems = (value: string) =>
  value
    .replace(/^[({]|[)}]$/g, "")
    .split(",")
    .map((item) => item.trim().replace(/^"|"$/g, ""))
    .filter(Boolean);

export const activeFilterChips = (
  filterValues: Record<string, unknown>,
): FilterChip[] => {
  const known = new Set(KINDS.map(([key]) => key));
  const chips = KINDS.flatMap(([key, kind]): FilterChip[] => {
    const value = filterValues[key];
    if (empty(value)) return [];
    // Montants en millions d'euros ; un autre « ou » reste une étiquette « Autre filtre ».
    if (kind === "min_eur" || kind === "min_eur_or_unknown") {
      const amount = readAmountFilter({ [key]: value });
      return amount
        ? [{ key, kind, values: [String(amount.millions)] }]
        : [{ key, kind: "other", values: [String(value)] }];
    }
    if (kind === "max_eur")
      return [{ key, kind, values: [String(Number(value) / 1_000_000)] }];
    const text = String(value);
    return [
      {
        key,
        kind,
        // Listes de PostgREST : (a,b) pour « parmi », {a} pour « contient ».
        values:
          key.endsWith("@in") || key.endsWith("@cs") ? listItems(text) : [text],
      },
    ];
  });
  // Un filtre que l'en-tête ne sait pas nommer reste visible et retirable.
  const others = Object.entries(filterValues)
    .filter(([key, value]) => !known.has(key) && !empty(value))
    .map(
      ([key, value]): FilterChip => ({
        key,
        kind: "other",
        values: [String(value)],
      }),
    );
  return [...chips, ...others];
};

export const withoutChip = (
  filterValues: Record<string, unknown>,
  key: string,
) =>
  Object.fromEntries(
    Object.entries(filterValues).filter(([name]) => name !== key),
  );
